import {
  map,
  filter,
  some,
  includes,
  isEmpty,
  endsWith,
  get,
  has,
  forEach,
  reduce,
  keys,
  omit,
  pick,
  compact,
  last,
  isNaN,
  isNil,
} from 'lodash';
import { expandExcelColumns, downloadFile } from '@sharedModules/data/utils/export-utils';
import timeFlexibleEndingsMap from '@enums/time-flexible-endings';
import numberFormats from '@enums/number-formats';
import { useZones } from '@enums/feature-flags';

import { getTsgKeyFromColIdRegex } from './tsg-utils';

/**
 * Gets filtered columns for export
 * @param {Object} gridColumnApi - The grid's column API
 * @param {Array} columnsToExclude - Column patterns to exclude
 * @returns {Array} Filtered column IDs
 */
export const getFilteredColumnsForExport = (
  gridColumnApi,
  columnsToExclude = ['weekPriceChange']
) => {
  const visibleColumns = map(gridColumnApi.getAllDisplayedColumns(), column => column.getColId());

  // First filter out excluded columns
  const filteredColumns = filter(
    visibleColumns,
    columnId => !some(columnsToExclude, pattern => includes(columnId, pattern))
  );

  // Sort columns to put filteredAlerts at the end of each group
  return filteredColumns.sort((a, b) => {
    // Extract toolStoreGroup ID from column ID
    const groupA = getTsgKeyFromColIdRegex(a);
    const groupB = getTsgKeyFromColIdRegex(b);

    // If both columns belong to the same group
    if (groupA && groupB && groupA === groupB) {
      // Put filteredAlerts at the end
      if (a.endsWith('filteredAlerts')) return 1;
      if (b.endsWith('filteredAlerts')) return -1;
    }

    // Handle standalone filteredAlerts (not part of a group)
    if (a === 'filteredAlerts') return 1;
    if (b === 'filteredAlerts') return -1;

    // Otherwise keep original order
    return filteredColumns.indexOf(a) - filteredColumns.indexOf(b);
  });
};

/**
 * Creates a handler for scenario price columns
 * @param {Function} getItemData - Function to get item data with proper context
 * @param {Function} formatEffectiveDateRange - Function to format effective date range
 * @returns {Object} Scenario price handler
 */
const createValueFormatHandler = (getItemData, formatEffectiveDateRange) => {
  const formatPriceValidationExportValue = value => {
    if (Array.isArray(value)) {
      return compact(value.map(validation => get(validation, 'shorthand'))).join(', ');
    }

    if (value && typeof value === 'object') {
      return get(value, 'shorthand', '');
    }

    return value;
  };

  return {
    processCell: params => {
      const colId = params.column.getColId();

      if (endsWith(colId, 'scenarioPrice')) {
        const cellProps = get(params, 'value.props', {});
        const itemData = getItemData(cellProps.valueParams);
        return cellProps.useOverridePrice(itemData)
          ? cellProps.getOverridePrice(itemData)
          : get(itemData, cellProps.priceField);
      }

      if (endsWith(colId, 'effectiveDateRange')) {
        return formatEffectiveDateRange({
          ...params.column,
          data: params.node.data,
          value: params.value,
        });
      }

      if (colId === 'priceValidations') {
        return formatPriceValidationExportValue(params.value);
      }

      return null;
    },
    processHeader: params => {
      const colId = params.column.getColId();
      // In the UI, only the expiry date is shown, but export includes full range, so needs a different header.
      if (endsWith(colId, 'effectiveDateRange')) {
        return 'Override Duration';
      }
      return null;
    },
  };
};

/**
 * Creates a handler for alert columns with filtering for historical data alerts
 * @param {Object} alertTypes - Alert type definitions
 * @param {Function} translate - Translation function (i18n)
 * @param {String} selectedHistoricalPeriod - Selected historical period for filtering alerts
 * @returns {Object} Alert column handler
 */
const createAlertColumnHandler = (alertTypes, translate, selectedHistoricalPeriod = '') => {
  const headers = {};
  const columns = {};

  // Filter alert types based on historical period
  const timeFlexibleAlerts = keys(timeFlexibleEndingsMap).reduce((acc, ending) => {
    const endingLabel = translate(`alerts.${ending}`);
    const alertText = `${translate('alerts.missingHistoricalData')}: ${endingLabel}`;
    acc[`missingHistoricalData${ending}`] = alertText;
    return acc;
  }, {});

  const filteredAlertTypes = {
    ...omit(alertTypes, 'missingHistoricalData'),
    ...pick(timeFlexibleAlerts, [`missingHistoricalData${selectedHistoricalPeriod}`]),
  };

  // Create translation map for filtered alert types
  const alertTranslationMap = reduce(
    filteredAlertTypes,
    (result, alertType, key) => {
      result[key] = key.includes('missingHistoricalData') ? alertType : translate(`alerts.${key}`);
      return result;
    },
    {}
  );

  const createAlertMetadataMap = filteredAlerts => {
    // Initialize with null values for all filtered alert types
    const alertMetadataMap = reduce(
      filteredAlertTypes,
      (result, _, alertType) => {
        result[alertType] = null;
        return result;
      },
      {}
    );

    // Fill in metadata for alerts that exist
    forEach(filteredAlerts, alert => {
      if (alert.alertType && has(alertMetadataMap, alert.alertType)) {
        alertMetadataMap[alert.alertType] = alert.metadata || null;
      }
    });

    return alertMetadataMap;
  };

  return {
    headers,
    columns,
    processCell: params => {
      const colId = params.column.getColId();

      if (includes(colId, 'filteredAlerts')) {
        const filteredAlerts = get(params, 'value.props.product.filteredAlerts', []);
        if (filteredAlerts && filteredAlerts.length > 0) {
          const key = `${colId}-${params.accumulatedRowIndex}`;
          columns[key] = createAlertMetadataMap(filteredAlerts);
          return key;
        }
      }

      return null;
    },
    processHeader: params => {
      const colId = params.column.getColId();

      // Get column group prefix from specified path
      const groupName = get(params.column, 'originalParent.originalParent.colGroupDef.headerName');

      if (includes(colId, 'filteredAlerts')) {
        headers[colId] = map(keys(filteredAlertTypes), alertType => ({
          type: alertType,
          name: compact([groupName, alertTranslationMap[alertType]]).join(' - '),
        }));
        return colId;
      }
      return null;
    },
  };
};

/**
 * Exports grid data with handlers
 * @param {Object} gridApi - The grid API
 * @param {Object} options - Export options
 * @returns {Blob} Excel file as a blob
 */
const exportGridWithHandlers = (
  gridApi,
  { columnKeys, cellHandlers, headerHandlers },
  translate
) => {
  return gridApi.getDataAsExcel({
    columnKeys,
    sheetName: translate('pricing.exportSheetName'),
    skipColumnGroupHeaders: true,

    processCellCallback: params => {
      let result = null;
      forEach(cellHandlers, handler => {
        if (result === null) {
          const handlerResult = handler(params);
          if (handlerResult !== null) {
            result = handlerResult;
          }
        }
      });
      return result !== null ? result : params.value;
    },

    processHeaderCallback: params => {
      let result = null;
      forEach(headerHandlers, handler => {
        if (result === null) {
          const handlerResult = handler(params);
          if (handlerResult !== null) {
            result = handlerResult;
          }
        }
      });

      // Get the header name (either from custom handler or column definition)
      const headerName = result !== null ? result : params.column.getColDef().headerName;

      const columnGroupHeaderToInclude = [
        translate('pricing.mainHeaders.cost'),
        translate('pricing.mainHeaders.prices'),
        translate('pricing.mainHeaders.transferPrice'),
        translate('pricing.mainHeaders.purchasePrice'),
      ];

      // Get column group prefix from specified path
      const toolStoreGroupName = get(
        params.column,
        'originalParent.originalParent.colGroupDef.headerName'
      );
      const columnGroupName = get(params.column, 'originalParent.colGroupDef.headerName');

      // If group name name exists, add it as prefix to the header name
      if (toolStoreGroupName) {
        return compact([
          toolStoreGroupName && `${toolStoreGroupName} -`,
          columnGroupName && columnGroupHeaderToInclude.includes(columnGroupName)
            ? `${columnGroupName} -`
            : '',
          headerName,
        ]).join(' ');
      }

      return headerName;
    },
  });
};

/**
 * Checks if a column is the last column in a specified group
 * @param {Object} params - Column parameters
 * @param {String} groupName - Name of the column group to check
 * @returns {Boolean} True if this is the last column in the specified group
 */
const isLastColumnInGroup = (params, groupName) => {
  // Get column group info
  const column = params.column;
  const columnGroup = get(column, 'originalParent');

  if (!columnGroup) return false;

  // Check if this is the specified column group
  const currentGroupName = get(columnGroup, 'colGroupDef.headerGroupComponentParams.headerName');
  if (currentGroupName !== groupName) {
    return false;
  }

  // Get last column id within this group
  const lastColumnId = get(last(get(columnGroup, 'colGroupDef.children', [])), 'colId');

  // Check if this is the last column
  return column.getColId() === lastColumnId;
};

const usesNestedPriceStructure = ({ fieldPath, matchedPriceField, rowData, basePath }) => {
  return (
    includes(fieldPath, `${matchedPriceField}.`) ||
    has(rowData, `${basePath}${matchedPriceField}.price`)
  );
};

/**
 * Adds norm weight column data for any price field type (UoM and norm weight variants)
 * @param {Object} options - Norm weight column options
 */
const addNormWeightColumnData = options => {
  const {
    rowData,
    basePath,
    priceData,
    headersList,
    groupName,
    columnHeaderName,
    translate,
    isFeatureFlagEnabled,
    priceField,
    isNested,
    includeNormWeightColumn,
    priceType,
    formatNumber,
  } = options;

  // Only add if feature flag is enabled
  if (!isFeatureFlagEnabled || !isFeatureFlagEnabled('includeNormWeight')) {
    return;
  }

  const variants = [
    {
      suffix: 'PerContentUnit',
      translationKey: 'gridView.priceToggles.pricePerContentUnit',
    },
    {
      suffix: 'PerNormWeight',
      translationKey: 'gridView.priceToggles.pricePerNormWeight',
    },
  ];

  // Add norm weight column
  if (includeNormWeightColumn && rowData && priceData) {
    priceData.normWeight = get(rowData, `${basePath}normWeight`);
  }

  if (includeNormWeightColumn && headersList && translate) {
    headersList.push({
      type: 'normWeight',
      name: compact([groupName, translate('pricing.normWeight')]).join(' - '),
    });
  }

  // Add variant data values
  if (rowData && priceData) {
    variants.forEach(variant => {
      const fieldPath = `${priceField}${isNested ? '.price' : ''}${variant.suffix}`;
      const value = get(rowData, `${basePath}${fieldPath}`);
      const formattedValue = formatNumber({
        number: value,
        format: priceType ? numberFormats.priceFormat : numberFormats.costFormat,
      });
      priceData[fieldPath] = isNaN(value) || isNil(value) ? '' : formattedValue;
    });
  }

  // Add variant headers
  if (headersList && translate) {
    variants.forEach(variant => {
      const headerText = `${columnHeaderName} ${translate(variant.translationKey)}`;
      headersList.push({
        type: `${priceField}${isNested ? '.price' : ''}${variant.suffix}`,
        name: compact([groupName, headerText]).join(' - '),
      });
    });
  }
};

/**
 * Adds impact columns (sales and cost) to data and headers
 * @param {Object} options - Column options
 */
const addImpactColumns = options => {
  const { rowData, basePath, priceData, headersList, groupName, translate } = options;

  // Define all impact fields to add
  const impactFields = [
    'scenarioSalesImpact',
    'regularSalesImpact',
    'scenarioCostImpact',
    'regularCostImpact',
  ];

  // Map fields to translation keys
  const translationKeys = {
    scenarioSalesImpact: 'pricing.exportTotalSalesImpact',
    regularSalesImpact: 'pricing.exportRegularSalesImpact',
    scenarioCostImpact: 'pricing.exportTotalCostImpact',
    regularCostImpact: 'pricing.exportRegularCostImpact',
  };

  // Add all impact data values
  if (rowData && priceData) {
    impactFields.forEach(field => {
      priceData[field] = get(rowData, `${basePath}${field}`);
    });
  }

  // Add all impact headers
  if (headersList && translate) {
    impactFields.forEach(field => {
      headersList.push({
        type: field,
        name: compact([groupName, translate(translationKeys[field])]).join(' - '),
      });
    });
  }
};

/**
 * Adds reference price columns to data and headers
 * @param {Object} options - Column options
 */
const addReferencePriceColumns = options => {
  const {
    rowData,
    basePath,
    priceData,
    headersList,
    groupName,
    translate,
    isFeatureFlagEnabled,
  } = options;

  // Define reference price types
  const refPriceTypes = [
    'economicReferencePrice',
    'competitorReferencePrice',
    'architectureReferencePrice',
  ];

  // Add store group reference price if feature flag enabled
  if (isFeatureFlagEnabled && isFeatureFlagEnabled(useZones)) {
    refPriceTypes.push('storeGroupReferencePrice');
  }

  // Add minMax reference if feature flag enabled
  if (isFeatureFlagEnabled && isFeatureFlagEnabled('displayMinMaxPenalty')) {
    refPriceTypes.push('minMaxReferencePrice');
  }

  // Add reference price data values
  if (rowData && priceData) {
    refPriceTypes.forEach(refType => {
      priceData[refType] = get(rowData, `${basePath}${refType}`);
    });
  }

  // Add reference price headers
  if (headersList && translate) {
    refPriceTypes.forEach(refType => {
      headersList.push({
        type: refType,
        name: compact([groupName, translate(`pricing.${refType}`)]).join(' - '),
      });
    });
  }
};

/**
 * Adds all proposed pricing columns in the correct order
 * @param {Object} options - Column options
 */
const addProposedPriceColumns = options => {
  const {
    params,
    rowData,
    priceData,
    headersList,
    translate,
    isFeatureFlagEnabled,
    formatNumber,
  } = options;

  const colId = params.column.getColId();
  // Get base path for data and group name for headers
  const basePath = get(colId.match(/(.*?toolStoreGroups\.[^.]+\.)/), 1, '');
  const groupName = get(params.column, 'originalParent.originalParent.colGroupDef.headerName');

  const columnOptions = {
    rowData,
    basePath,
    priceData,
    headersList,
    groupName,
    translate,
    isFeatureFlagEnabled,
  };

  // 1. Add impact columns first
  addImpactColumns(columnOptions);

  // 2. Add reference price columns next
  addReferencePriceColumns(columnOptions);

  // 3. Add scenario norm weight columns last
  addNormWeightColumnData({
    ...columnOptions,
    priceField: 'scenarioPrice',
    includeNormWeightColumn: true,
    columnHeaderName: translate('pricing.scenarioPrice'),
    priceType: true,
    formatNumber,
  });

  return { priceData, headersList };
};

/**
 * Creates a handler for the proposed pricing column group
 * @param {Function} translate - Translation function (i18n)
 * @param {Function} isFeatureFlagEnabled - Function to check if feature flag is enabled
 * @returns {Object} Proposed pricing group handler
 */
const createProposedPricingGroupEndHandler = (translate, isFeatureFlagEnabled, formatNumber) => {
  const headers = {};
  const columns = {};

  return {
    headers,
    columns,
    processCell: params => {
      // Only process the last column in the Proposed Pricing group
      if (!isLastColumnInGroup(params, translate('pricing.mainHeaders.proposedPricing'))) {
        return null;
      }

      const colId = params.column.getColId();
      const rowData = params.node.data;

      const priceData = {};

      // Process additional columns for this cell
      const result = addProposedPriceColumns({
        params,
        rowData,
        priceData,
        translate,
        isFeatureFlagEnabled,
        formatNumber,
      });

      // Store with unique key
      const key = `${colId}-groupEnd-${params.accumulatedRowIndex}`;
      columns[key] = result.priceData;
      return key;
    },

    processHeader: params => {
      // Only process the last column in the Proposed Pricing group
      if (!isLastColumnInGroup(params, translate('pricing.mainHeaders.proposedPricing'))) {
        return null;
      }

      const colId = params.column.getColId();
      const headersList = [];

      // Process additional column headers
      const result = addProposedPriceColumns({
        params,
        headersList,
        translate,
        isFeatureFlagEnabled,
      });

      // Store headers
      headers[colId] = result.headersList;
      return colId;
    },
  };
};

/**
 * Creates a handler for price columns to export all price types
 * @param {Function} translate - Translation function (i18n)
 * @param {Function} isFeatureFlagEnabled - Function to check if feature flag is enabled
 * @returns {Object} Price column handler
 */
export const createNormWeightColumnHandler = (translate, isFeatureFlagEnabled, formatNumber) => {
  const headers = {};
  const columns = {};

  // Price and cost fields that need expansion - excluding scenarioPrice which is handled separately
  const priceFields = ['livePrice', 'intentionPrice'];
  const costFields = ['liveCost', 'nonPromoNetCost'];

  return {
    headers,
    columns,
    processCell: params => {
      const colDef = params.column.getColDef();
      const colId = params.column.getColId();

      // Skip if no field defined
      if (!colDef.field) return null;

      // Find matching price field
      const matchedPriceField = [...priceFields, ...costFields].find(field =>
        includes(colDef.field, field)
      );
      if (!matchedPriceField) return null;

      // Extract base path and determine structure format
      const basePath = colDef.field.substring(0, colDef.field.indexOf(matchedPriceField));

      // Get row data and collect price data
      const rowData = params.node.data;
      const priceData = {};
      const isNested = usesNestedPriceStructure({
        fieldPath: colDef.field,
        matchedPriceField,
        rowData,
        basePath,
      });

      // Get column group prefix from specified path
      const groupName = get(params.column, 'originalParent.originalParent.colGroupDef.headerName');

      // Add base price value
      const value = isNested
        ? get(rowData, `${basePath}${matchedPriceField}.price`)
        : get(rowData, `${basePath}${matchedPriceField}`);

      const priceType = priceFields.includes(matchedPriceField);
      const formattedValue = formatNumber({
        number: value,
        format: priceType ? numberFormats.priceFormat : numberFormats.costFormat,
      });

      priceData.regular = isNaN(value) || isNil(value) ? '' : formattedValue;

      // Add norm weight column data using the common function
      addNormWeightColumnData({
        rowData,
        basePath,
        priceData,
        headersList: [], // Not needed for cell processing
        groupName,
        columnHeaderName: colDef.headerName,
        translate,
        isFeatureFlagEnabled,
        priceField: matchedPriceField,
        isNested,
        priceType,
        formatNumber,
      });

      // Store with unique key
      const key = `${colId}-${params.accumulatedRowIndex}`;
      columns[key] = priceData;
      return key;
    },

    processHeader: params => {
      const colDef = params.column.getColDef();
      const colId = params.column.getColId();

      // Skip if no field defined
      if (!colDef.field) return null;

      // Find matching price field
      const matchedPriceField = [...priceFields, ...costFields].find(field =>
        includes(colDef.field, field)
      );
      if (!matchedPriceField) return null;

      const isNested = usesNestedPriceStructure({
        fieldPath: colDef.field,
        matchedPriceField,
        rowData: null,
        basePath: '',
      });

      // Get column group prefix from specified path
      const toolStoreGroupName = get(
        params.column,
        'originalParent.originalParent.colGroupDef.headerName'
      );
      const columnGroupName = get(params.column, 'originalParent.colGroupDef.headerName');
      const headerName = get(colDef, 'headerName');

      // Start with basic price header
      const headersList = [
        {
          type: 'regular',
          name: compact([toolStoreGroupName, columnGroupName, headerName]).join(' - '),
        },
      ];

      // Add norm weight column data using the common function
      addNormWeightColumnData({
        headersList,
        groupName: compact([toolStoreGroupName, columnGroupName]).join(' - '),
        columnHeaderName: colDef.headerName,
        translate,
        isFeatureFlagEnabled,
        priceField: matchedPriceField,
        isNested,
      });

      // Store the header list
      headers[colId] = headersList;
      return colId;
    },
  };
};

/**
 * Exports grid data with added columns with custom columns
 * @param {Object} options - Options for export
 */
export const exportGridWithCustomColumns = ({
  gridApi,
  gridColumnApi,
  getItemData,
  alertTypes,
  translate,
  fileName = 'workpackage_product_data.xlsx',
  columnsToExclude,
  selectedHistoricalPeriod = '',
  isFeatureFlagEnabled,
  formatEffectiveDateRange,
  formatNumber,
}) => {
  const filteredColumns = getFilteredColumnsForExport(gridColumnApi, columnsToExclude);

  // Set up column handlers for special column types
  const valueFormatHandler = createValueFormatHandler(getItemData, formatEffectiveDateRange);
  const alertHandler = createAlertColumnHandler(alertTypes, translate, selectedHistoricalPeriod);
  const normWeightColumnsHandler = createNormWeightColumnHandler(
    translate,
    isFeatureFlagEnabled,
    formatNumber
  );
  const proposedPricingGroupEndHandler = createProposedPricingGroupEndHandler(
    translate,
    isFeatureFlagEnabled,
    formatNumber
  );

  // Perform the export with custom column processing
  const defaultExport = exportGridWithHandlers(
    gridApi,
    {
      columnKeys: filteredColumns,
      cellHandlers: [
        alertHandler.processCell,
        normWeightColumnsHandler.processCell,
        valueFormatHandler.processCell,
        proposedPricingGroupEndHandler.processCell,
      ],
      headerHandlers: [
        alertHandler.processHeader,
        normWeightColumnsHandler.processHeader,
        valueFormatHandler.processHeader,
        proposedPricingGroupEndHandler.processHeader,
      ],
    },
    translate
  );

  // Process the export with added custom columns
  const hasColumnsToExpand =
    !isEmpty(alertHandler.headers) ||
    !isEmpty(normWeightColumnsHandler.headers) ||
    !isEmpty(proposedPricingGroupEndHandler.headers);

  if (hasColumnsToExpand) {
    expandExcelColumns(
      defaultExport,
      {
        ...alertHandler.headers,
        ...normWeightColumnsHandler.headers,
        ...proposedPricingGroupEndHandler.headers,
      },
      {
        ...alertHandler.columns,
        ...normWeightColumnsHandler.columns,
        ...proposedPricingGroupEndHandler.columns,
      }
    )
      .then(processedBlob => {
        downloadFile(processedBlob, fileName);
      })
      .catch(error => {
        console.error('Error expanding Excel columns:', error);
        downloadFile(defaultExport, fileName);
      });
  } else {
    downloadFile(defaultExport, fileName);
  }
};

export default exportGridWithCustomColumns;
